// filepath: frontend/src/components/CommandPalette.tsx
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Upload, ExternalLink, Download, Package, FileText, BarChart2 } from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { api } from '@/lib/api';
import { PaginatedResult, Shipment } from '@/types';

interface Command {
  id: string;
  label: string;
  shortcut?: string;
  icon: React.ReactNode;
  action: () => void;
  roles?: string[];
}

const jiraBaseUrl = import.meta.env.VITE_JIRA_BASE_URL || 'https://your-company.atlassian.net';

export function CommandPalette() {
  const { commandPaletteOpen, setCommandPaletteOpen, recentShipments } = useUIStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => {
    setCommandPaletteOpen(false);
    setQuery('');
    setSelectedIndex(0);
  };
  const shipmentRoutePrefix = user?.role === 'account_manager' || user?.role === 'ceo' ? '/account/shipments' : '/shipments';
  const { data: shipmentSearch } = useQuery<PaginatedResult<Shipment>>({
    queryKey: ['command-shipment-search', query],
    enabled: commandPaletteOpen && query.trim().length >= 2,
    queryFn: () =>
      api.get('/shipments', {
        params: { search: query.trim(), limit: 5, page: 1 },
      }).then((r) => r.data as PaginatedResult<Shipment>),
  });

  const commands: Command[] = [
    {
      id: 'upload',
      label: 'Upload Document',
      shortcut: '⌘U',
      icon: <Upload size={16} />,
      action: () => { navigate('/shipments'); close(); },
    },
    {
      id: 'jira',
      label: 'Open Jira',
      shortcut: '⌘J',
      icon: <ExternalLink size={16} />,
      action: () => { window.open(jiraBaseUrl, '_blank'); close(); },
      roles: ['account_manager', 'filing_clerk', 'ceo'],
    },
    {
      id: 'shipments',
      label: 'View All Shipments',
      icon: <Package size={16} />,
      action: () => { navigate(user?.role === 'account_manager' || user?.role === 'ceo' ? '/account/shipments' : '/shipments'); close(); },
    },
    {
      id: 'tariff-lookup',
      label: 'Tariff Quick Lookup',
      icon: <Search size={16} />,
      action: () => { navigate('/filing/tariff'); close(); },
      roles: ['account_manager', 'filing_clerk', 'ceo'],
    },
    {
      id: 'help',
      label: 'Help Centre',
      icon: <FileText size={16} />,
      action: () => { navigate('/help'); close(); },
    },
  ];

  const filtered = commands
    .filter((c) => !c.roles || (user && c.roles.includes(user.role)))
    .filter((c) => !query || c.label.toLowerCase().includes(query.toLowerCase()));

  // Recent shipments as commands
  const recentCmds = recentShipments
    .filter((s) => !query || s.reference.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 5)
    .map((s) => ({
      id: `recent-${s.id}`,
      label: s.reference,
      icon: <Package size={16} />,
      action: () => { navigate(`${shipmentRoutePrefix}/${s.id}`); close(); },
    }));
  const searchedShipmentCmds = (shipmentSearch?.data ?? [])
    .filter((s) => !recentShipments.some((r) => r.id === s.id))
    .map((s) => ({
      id: `search-${s.id}`,
      label: `${s.reference} · ${s.client?.name ?? ''}`,
      icon: <Package size={16} />,
      action: () => { navigate(`${shipmentRoutePrefix}/${s.id}`); close(); },
    }));
  const allResults = [...recentCmds, ...searchedShipmentCmds, ...filtered];

  useEffect(() => {
    if (commandPaletteOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setSelectedIndex(0);
    }
  }, [commandPaletteOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (!commandPaletteOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (allResults.length === 0 ? 0 : (prev + 1) % allResults.length));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (allResults.length === 0 ? 0 : (prev - 1 + allResults.length) % allResults.length));
      }
      if (e.key === 'Enter' && allResults[selectedIndex]) {
        e.preventDefault();
        allResults[selectedIndex].action();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [allResults, commandPaletteOpen, selectedIndex]);

  return (
    <AnimatePresence>
      {commandPaletteOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50"
            style={{ background: 'rgba(0,0,0,0.6)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />

          {/* Palette */}
          <motion.div
            className="fixed top-[20vh] left-1/2 -translate-x-1/2 z-50 w-full max-w-xl glass rounded-xl overflow-hidden"
            initial={{ opacity: 0, y: -16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            role="dialog"
            aria-label="Command palette"
            aria-modal="true"
          >
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <Search size={18} style={{ color: '#838383' }} aria-hidden="true" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search commands, shipments..."
                className="flex-1 bg-transparent outline-none text-white placeholder-gray-500 text-sm"
                aria-label="Command search"
              />
              <kbd className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.08)', color: '#838383' }}>ESC</kbd>
            </div>

            {/* Results */}
            <div className="max-h-80 overflow-y-auto py-2" role="listbox">
              {recentCmds.length > 0 && (
                <>
                  <div className="px-4 py-1 text-xs font-semibold uppercase tracking-wider" style={{ color: '#838383' }}>
                    Recent Shipments
                  </div>
                  {recentCmds.map((cmd, index) => (
                    <CommandItem key={cmd.id} cmd={cmd} selected={selectedIndex === index} />
                  ))}
                  <div className="my-1 mx-4" style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
                </>
              )}

              {searchedShipmentCmds.length > 0 && (
                <>
                  <div className="px-4 py-1 text-xs font-semibold uppercase tracking-wider" style={{ color: '#838383' }}>
                    Shipments
                  </div>
                  {searchedShipmentCmds.map((cmd, index) => (
                    <CommandItem key={cmd.id} cmd={cmd} selected={selectedIndex === index + recentCmds.length} />
                  ))}
                  <div className="my-1 mx-4" style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
                </>
              )}

              {filtered.length > 0 && (
                <>
                  <div className="px-4 py-1 text-xs font-semibold uppercase tracking-wider" style={{ color: '#838383' }}>
                    Commands
                  </div>
                  {filtered.map((cmd, index) => (
                    <CommandItem key={cmd.id} cmd={cmd} selected={selectedIndex === index + recentCmds.length + searchedShipmentCmds.length} />
                  ))}
                </>
              )}

              {filtered.length === 0 && recentCmds.length === 0 && (
                <div className="px-4 py-8 text-center text-sm" style={{ color: '#838383' }}>
                  No results for "{query}"
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function CommandItem({
  cmd,
  selected,
}: {
  cmd: { id: string; label: string; shortcut?: string; icon: React.ReactNode; action: () => void };
  selected?: boolean;
}) {
  return (
    <button
      key={cmd.id}
      onClick={cmd.action}
      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-white/5"
      style={selected ? { background: 'rgba(255,255,255,0.08)' } : undefined}
      role="option"
      aria-selected={selected}
    >
      <span style={{ color: '#E47527' }}>{cmd.icon}</span>
      <span className="flex-1 text-white">{cmd.label}</span>
      {cmd.shortcut && (
        <kbd className="text-xs px-1.5 py-0.5 rounded font-mono" style={{ background: 'rgba(255,255,255,0.08)', color: '#838383' }}>
          {cmd.shortcut}
        </kbd>
      )}
    </button>
  );
}
